import { z } from 'zod';

/**
 * Exportacao contabil — a ponte entre a classificacao e a contabilidade.
 *
 * A regra de partida dobrada mora aqui, no pacote compartilhado, e nao na rota,
 * porque ela e usada duas vezes: o servidor gera o XLSX com ela e a tela mostra
 * a previa com ela. Se cada lado tivesse a sua, a previa poderia concordar com
 * a pessoa e discordar do arquivo — que e o unico jeito de esse erro passar
 * despercebido ate chegar no contador.
 *
 * O layout e fixo: data | debito | credito | valor | cod hist | hist | cc.
 */

/** Codigo de historico padrao da casa. Constante, nao configuravel por ora. */
export const COD_HISTORICO = 10;

/** Dias que a provisao antecede a baixa. Regra da casa, fixa. */
export const DIAS_DE_PROVISAO = 30;

/** Uma linha do arquivo, ja no formato final. */
export interface LinhaContabil {
  /** dd/mm/aaaa. */
  data: string;
  /** Codigo contabil da conta debitada. */
  debito: string;
  /** Codigo contabil da conta creditada. */
  credito: string;
  /** Sempre positivo — o sinal ja esta expresso por qual lado cada conta ocupa. */
  valor: number;
  cod_hist: number;
  hist: string;
  /** Centro de custo: sempre vazio hoje. */
  cc: string;
}

/** O lancamento com tudo que a regra precisa, ja resolvido pelo servidor. */
export interface LancamentoExportavel {
  id: number;
  bank_id: number;
  /** dd/mm/aaaa — data do extrato (`posted_date`). */
  posted_date: string;
  /** dd/mm/aaaa — `posted_date` menos DIAS_DE_PROVISAO, calculado no banco. */
  data_provisao: string;
  /** Negativo = saida, positivo = entrada. */
  amount: number;
  description: string | null;
  /** Texto livre que a pessoa escreveu na classificacao. Entra no historico. */
  complemento: string | null;
  /** Nome da conta do plano financeiro (`transactions.category`). */
  category: string | null;
  /** Codigo contabil da conta do plano. */
  conta_codigo: string | null;
  banco_nome: string;
  banco_codigo: string | null;
  provisiona: boolean;
  patrimonial_account_id: number | null;
  patrimonial_nome: string | null;
  patrimonial_codigo: string | null;
}

export const PENDENCIAS = [
  'sem_classificacao',
  'sem_conta_contabil',
  'sem_codigo_banco',
  'provisao_sem_patrimonial',
  'sem_codigo_patrimonial',
  'valor_zero',
] as const;

export type Pendencia = (typeof PENDENCIAS)[number];

/** O que falta e onde resolver. O `caminho` vira link na faixa de pendencias. */
export const DETALHE_DA_PENDENCIA: Record<
  Pendencia,
  { rotulo: string; caminho: string | null }
> = {
  sem_classificacao: {
    rotulo: 'Lancamento sem conta do plano financeiro.',
    caminho: '/classificacao',
  },
  sem_conta_contabil: {
    rotulo: 'A conta do plano nao tem codigo contabil.',
    caminho: '/plano',
  },
  sem_codigo_banco: {
    rotulo: 'O banco nao tem codigo contabil.',
    caminho: '/bancos-contabeis',
  },
  provisao_sem_patrimonial: {
    rotulo: 'Provisao ligada sem conta patrimonial escolhida.',
    caminho: null,
  },
  sem_codigo_patrimonial: {
    rotulo: 'A conta patrimonial nao tem codigo contabil.',
    caminho: '/patrimoniais',
  },
  valor_zero: { rotulo: 'Lancamento de valor zero.', caminho: '/classificacao' },
};

/**
 * O que impede este lancamento de virar partida.
 *
 * Devolver a lista, e nao um booleano, e o que permite a tela dizer o que falta
 * e onde arrumar. Um codigo faltando vira celula vazia no XLSX e erro silencioso
 * na importacao contabil — por isso a exportacao bloqueia em vez de exportar.
 */
export function pendenciasDaLinha(l: LancamentoExportavel): Pendencia[] {
  const faltando: Pendencia[] = [];
  if (!l.category) faltando.push('sem_classificacao');
  if (!l.conta_codigo) faltando.push('sem_conta_contabil');
  if (!l.banco_codigo) faltando.push('sem_codigo_banco');
  if (l.amount === 0) faltando.push('valor_zero');
  if (l.provisiona) {
    if (!l.patrimonial_account_id) faltando.push('provisao_sem_patrimonial');
    else if (!l.patrimonial_codigo) faltando.push('sem_codigo_patrimonial');
  }
  return faltando;
}

/** Separador entre a descricao do extrato e o complemento, no historico. */
export const SEPARADOR_DO_HISTORICO = ' - ';

/**
 * Historico do lancamento.
 *
 * A descricao vem do extrato e costuma ser cifrada ("TED 341 REM 0012"); o
 * complemento e o que a pessoa escreveu na classificacao para dar sentido a
 * ela. Os dois juntos e o que a contabilidade precisa ler, entao vao os dois,
 * nessa ordem — o complemento sozinho perderia a referencia do extrato, e a
 * descricao sozinha perderia a explicacao.
 *
 * Complemento vazio nao deixa separador solto no fim da celula. Sem nenhum dos
 * dois, cai no nome da conta: melhor um historico generico que uma celula em
 * branco no lote enviado ao contador.
 */
export function historicoDaLinha(l: LancamentoExportavel): string {
  const partes = [l.description, l.complemento]
    .map((parte) => parte?.trim() ?? '')
    .filter((parte) => parte !== '');

  if (partes.length > 0) return partes.join(SEPARADOR_DO_HISTORICO);
  return l.category ?? '';
}

/**
 * As partidas de um lancamento.
 *
 * Sem provisao, uma linha: a contrapartida do plano e o proprio banco. Com
 * provisao, duas: o plano fecha contra a conta patrimonial na competencia
 * (30 dias antes), e a patrimonial fecha contra o banco no dia da liquidacao.
 *
 * O sinal decide de que lado o banco entra — negativo (saida) credita o banco,
 * positivo (entrada) debita. Todo o resto e o espelho disso. O valor vai sempre
 * absoluto: repetir o sinal produziria lancamento invalido, ja que o lado ja o
 * expressa.
 *
 * Devolve lista vazia quando ha pendencia — quem barra e a rota, mas montar
 * meia partida seria pior do que nao montar nenhuma.
 */
export function montarLinhasContabeis(l: LancamentoExportavel): LinhaContabil[] {
  if (pendenciasDaLinha(l).length > 0) return [];

  const plano = l.conta_codigo!;
  const banco = l.banco_codigo!;
  const valor = Math.round(Math.abs(l.amount) * 100) / 100;
  const entrada = l.amount > 0;
  const base = { valor, cod_hist: COD_HISTORICO, hist: historicoDaLinha(l), cc: '' };

  if (!l.provisiona) {
    return [
      entrada
        ? { data: l.posted_date, debito: banco, credito: plano, ...base }
        : { data: l.posted_date, debito: plano, credito: banco, ...base },
    ];
  }

  const patrimonial = l.patrimonial_codigo!;
  if (entrada) {
    return [
      { data: l.data_provisao, debito: patrimonial, credito: plano, ...base },
      { data: l.posted_date, debito: banco, credito: patrimonial, ...base },
    ];
  }
  return [
    { data: l.data_provisao, debito: plano, credito: patrimonial, ...base },
    { data: l.posted_date, debito: patrimonial, credito: banco, ...base },
  ];
}

/** Cabecalho do arquivo, na ordem das colunas de `LinhaContabil`. */
export const COLUNAS_DA_EXPORTACAO = [
  'data',
  'debito',
  'credito',
  'valor',
  'cod hist',
  'hist',
  'cc',
] as const;

export const FORMATOS_DA_EXPORTACAO = ['xlsx', 'csv'] as const;
export type FormatoDaExportacao = (typeof FORMATOS_DA_EXPORTACAO)[number];

/** Uma linha como lista de campos, na ordem do cabecalho. Serve os dois formatos. */
export function camposDaLinha(l: LinhaContabil): string[] {
  return [
    l.data,
    l.debito,
    l.credito,
    // Duas casas sempre, com PONTO decimal. A virgula ja e o separador de
    // coluna: escrever "1250,50" partiria o valor em duas colunas e o lote
    // entraria torto na contabilidade sem nenhum erro no caminho.
    l.valor.toFixed(2),
    String(l.cod_hist),
    l.hist,
    l.cc,
  ];
}

/**
 * Escapa um campo conforme a RFC 4180.
 *
 * So poe aspas quando precisa — campo com virgula, aspas ou quebra de linha.
 * O historico e o unico que costuma cair nessa regra, e e justamente onde uma
 * virgula no texto livre deslocaria todas as colunas seguintes.
 */
function campoCsv(valor: string): string {
  if (!/[",\r\n]/.test(valor)) return valor;
  return `"${valor.replace(/"/g, '""')}"`;
}

/**
 * As linhas contabeis como CSV separado por virgula.
 *
 * Fim de linha CRLF e cabecalho na primeira linha, como manda a RFC 4180. O
 * BOM de UTF-8 vai na frente porque sem ele o Excel abre o arquivo em Latin-1
 * e todo acento do historico vira caractere quebrado.
 */
export function montarCsv(linhas: readonly LinhaContabil[]): string {
  const cabecalho = COLUNAS_DA_EXPORTACAO.map(campoCsv).join(',');
  const corpo = linhas.map((l) => camposDaLinha(l).map(campoCsv).join(','));
  return `\uFEFF${[cabecalho, ...corpo].join('\r\n')}\r\n`;
}

/**
 * Uma linha contabil vinda da tela.
 *
 * Ate aqui o servidor derivava as partidas e o cliente so as exibia. Com as
 * colunas editaveis, quem manda e a tela: ela envia as linhas finais e o
 * servidor formata. E a unica forma de garantir que o arquivo e exatamente o
 * que a pessoa conferiu — rederivar no servidor ignoraria toda edicao.
 *
 * A validacao aqui e de forma, nao de contabilidade: campo obrigatorio
 * preenchido e valor positivo. O julgamento contabil e de quem edita, que foi
 * justamente o pedido.
 */
export const linhaContabil = z.object({
  data: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Data deve ser dd/mm/aaaa.'),
  debito: z.string().trim().min(1, 'Debito e obrigatorio.').max(32),
  credito: z.string().trim().min(1, 'Credito e obrigatorio.').max(32),
  valor: z.number().positive('Valor deve ser maior que zero.'),
  cod_hist: z.number().int().nonnegative(),
  hist: z.string().max(500),
  cc: z.string().max(64),
});

/** Teto de linhas por arquivo. Um mes real nao chega perto; corpo gigante, sim. */
export const MAX_LINHAS_DA_EXPORTACAO = 20000;

export const arquivoDaExportacaoBody = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  linhas: z
    .array(linhaContabil)
    .min(1, 'Nao ha linha para exportar.')
    .max(MAX_LINHAS_DA_EXPORTACAO, 'Linhas demais para um arquivo so.'),
});

export type ArquivoDaExportacaoBody = z.infer<typeof arquivoDaExportacaoBody>;

/**
 * Subtrai dias de uma data dd/mm/aaaa, devolvendo dd/mm/aaaa.
 *
 * O servidor calcula `data_provisao` no banco, com DATE_SUB. Quando a tela
 * permite corrigir a data do lancamento, a data da provisao precisa acompanhar
 * — e nao da para pedir ao banco a cada tecla digitada.
 *
 * Tudo em UTC de proposito: com fuso local, subtrair 30 dias atravessando o
 * horario de verao devolve o dia anterior ao correto.
 */
export function subtrairDias(data: string, dias: number): string {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(data.trim());
  if (!partes) return data;

  const [, dia, mes, ano] = partes;
  const base = Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
  const alvo = new Date(base - dias * 86400000);
  if (Number.isNaN(alvo.getTime())) return data;

  const dd = String(alvo.getUTCDate()).padStart(2, '0');
  const mm = String(alvo.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${String(alvo.getUTCFullYear())}`;
}

/** Linha em branco para preencher a mao, ja com o que da para aproveitar. */
export function linhaEmBranco(l: LancamentoExportavel): LinhaContabil {
  return {
    data: l.posted_date,
    debito: l.conta_codigo ?? '',
    credito: l.banco_codigo ?? '',
    valor: Math.round(Math.abs(l.amount) * 100) / 100,
    cod_hist: COD_HISTORICO,
    hist: historicoDaLinha(l),
    cc: '',
  };
}

/** O que falta nesta linha para ela poder ir ao arquivo. */
export function problemaDaLinha(l: LinhaContabil): string | null {
  const parsed = linhaContabil.safeParse(l);
  return parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Linha invalida.');
}

/** Marca a provisao de um lancamento. Ambos opcionais: a tela grava um por vez. */
export const provisaoBody = z
  .object({
    provisiona: z.boolean().optional(),
    patrimonial_account_id: z.number().int().positive().nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Envie ao menos um campo.',
  });

export type ProvisaoBody = z.infer<typeof provisaoBody>;
